import React, { useRef, useState, useEffect, useContext } from 'react';
import { Button, Card, Row, Col, Modal, Form } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import { CartContext } from '../../../../context/CartContext';
import { Is_Login } from '../../../../helper/IsLogin';
import { Rating } from '@mui/material';
import api from '../../../../helper/apiAdmin';
import { getServerURL } from '../../../../helper/envConfig';
import Layout from '../layout/Layout';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';

const ViewReviewDetail = () => {
    const { id } = useParams();
    const review_id = id;
    const isLoggedIn = Is_Login();
    const navigate = useNavigate();
    const { setMainLoder } = useContext(CartContext);
    const [reviewDetail, setReviewDetail] = useState(null);
    const [loading, setLoading] = useState(true);
    const player = useRef();

    // Edit functionality
    const [showEditModal, setShowEditModal] = useState(false);
    const [editReview, setEditReview] = useState({
        _id: '',
        title: '',
        reviewer_display_name: '',
        source_reviewer_name: '',
        content: '',
        heading: '',
        rating: 0,
        review_files: [],
        review_type: 0
    });
    
    // File handling states
    const [filesToRemove, setFilesToRemove] = useState([]);
    const [successSnackbarOpen, setSuccessSnackbarOpen] = useState(false);
    const [errorSnackbarOpen, setErrorSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');

    const serverURL = getServerURL();

    const startAnimation = () => {
        if (player.current) {
            player.current.play();
        }
    };

    const stopAnimation = () => {
        setLoading(false);
    };

    const getReviewDetail = async () => {
        startAnimation();
        try {
            const apiTyp = isLoggedIn ? api.postWithToken : api.post;
            if (review_id) {
                const response = await apiTyp(`${serverURL}api/product-review-detail`, {
                    review_id: review_id,
                });
                if (response.data.success) {
                    setReviewDetail(response.data.data);
                } else {
                    handleErrorSnackbar(response.data.message || "Failed to load review details");
                }
                stopAnimation();
            }
        } catch (error) {
            console.log(error);
            handleErrorSnackbar("An error occurred while loading review details");
            stopAnimation();
        }
    };
    
    useEffect(() => {
        getReviewDetail();
    }, [review_id, isLoggedIn]);

    const deleteReview = async () => {
        if (!window.confirm("Are you sure you want to delete this review? This action cannot be undone.")) {
            return;
        }

        setMainLoder(true);
        try {
            const response = await api.post(`${serverURL}api/delete-review`, { review_id: review_id });
            if (response.data.success) {
                handleSuccessSnackbar("Review deleted successfully");
                setTimeout(() => {
                    navigate(-1); // Go back to previous page
                }, 1500);
            } else {
                handleErrorSnackbar(response.data.message || "Failed to delete review");
            }
        } catch (error) {
            console.log(error);
            handleErrorSnackbar("An error occurred while deleting the review");
        } finally {
            setMainLoder(false);
        }
    };

    // Handle opening the edit modal
    const handleEditClick = () => {
        if (reviewDetail) {
            setEditReview({
                _id: reviewDetail._id,
                title: reviewDetail.title || '',
                reviewer_display_name: reviewDetail.reviewer_display_name || reviewDetail.source_reviewer_name || reviewDetail.user_id?.name || reviewDetail.title || "Shopper",
                source_reviewer_name: reviewDetail.source_reviewer_name || '',
                content: reviewDetail.content,
                rating: reviewDetail.rating,
                heading: reviewDetail.heading,
                review_files: reviewDetail.review_files || [],
                review_type: reviewDetail.review_type || 0,
                fileNames: [],
                newFiles: []
            });
            setFilesToRemove([]);
            setShowEditModal(true);
        }
    };

    // Handle form input changes
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setEditReview(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // Handle rating change
    const handleRatingChange = (event, newValue) => {
        setEditReview(prev => ({
            ...prev,
            rating: newValue
        }));
    };
    
    // Handle file selection
    const handlePhoto = (e) => {
        const files = e.target.files;
        const fileNames = Array.from(files).map(file => file.name);
        
        // Check if total files selected exceeds the limit
        if (fileNames.length > 5) {
            handleErrorSnackbar("You can only upload a maximum of 5 files. Please select fewer files.");
            e.target.value = null;
            return;
        }

        setEditReview(prev => ({
            ...prev,
            newFiles: files,
            fileNames: fileNames
        }));
    };
    
    // Mark existing file to be removed
    const handleRemoveExistingFile = (fileId) => {
        setFilesToRemove(prev => [...prev, fileId]);
    };
    
    // Restore a file marked for removal
    const handleRestoreFile = (fileId) => {
        setFilesToRemove(prev => prev.filter(id => id !== fileId));
    };

    // Submit edited review
    const submitEditedReview = async (e) => {
        e.preventDefault();
        setMainLoder(true);
        
        try {
            const formData = new FormData();
            formData.append('action', 'update');
            formData.append('_id', editReview._id);
            formData.append('title', editReview.title);
            formData.append('reviewer_display_name', editReview.reviewer_display_name);
            formData.append('heading', editReview.heading);
            formData.append('content', editReview.content);
            formData.append('rating', editReview.rating);
            
            // Add files to remove if any
            if (filesToRemove.length > 0) {
                formData.append('files_to_remove', JSON.stringify(filesToRemove));
            }
            
            // Handle file uploads and review type
            if (editReview.newFiles && editReview.newFiles.length > 0) {
                let isImage = false;
                let isVideo = false;
                
                for (let i = 0; i < editReview.newFiles.length; i++) {
                    formData.append('review_files', editReview.newFiles[i]);
                    if (editReview.newFiles[i].type.startsWith('image/')) {
                        isImage = true;
                    }
                    if (editReview.newFiles[i].type.startsWith('video/')) {
                        isVideo = true;
                    }
                }
                
                // Determine review type based on media type
                let reviewType = editReview.review_type;
                
                if (isImage && isVideo) {
                    reviewType = 3; // for both image and video
                } else if (isImage) {
                    reviewType = 1; // for image review
                } else if (isVideo) {
                    reviewType = 2; // for video review
                }
                
                formData.append('review_type', reviewType);
            }
            
            // Make the API call
            const response = await api.postWithToken(`${serverURL}api/product-review-manage`, formData);
            
            if (response?.data.success === true) {
                handleSuccessSnackbar("Review updated successfully");
                setShowEditModal(false);
                getReviewDetail(); // Refresh the review details
            } else {
                handleErrorSnackbar(response?.data.message || "Failed to update review");
            }
        } catch (error) {
            console.log(error);
            handleErrorSnackbar("An error occurred. Please try again.");
        } finally {
            setMainLoder(false);
        }
    };

    // Snackbar handlers
    const handleSnackbarClose = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }
        setSuccessSnackbarOpen(false);
        setErrorSnackbarOpen(false);
    };

    const handleSuccessSnackbar = (message) => {
        setSnackbarMessage(message || 'Operation successful');
        setSuccessSnackbarOpen(true);
        setTimeout(() => {
            setSuccessSnackbarOpen(false);
        }, 3000);
    };

    const handleErrorSnackbar = (errorMessage) => {
        setSnackbarMessage(errorMessage || 'An error occurred');
        setErrorSnackbarOpen(true);
        setTimeout(() => {
            setErrorSnackbarOpen(false);
        }, 3000);
    };

    if (loading) {
        return (
            <Layout>
                <div className="text-center py-5">
                    <p>Loading review details...</p>
                </div>
            </Layout>
        );
    }

    if (!reviewDetail) {
        return (
            <Layout>
                <div className="text-center py-5">
                    <p>Review not found</p>
                    <Button variant="primary" onClick={() => navigate(-1)}>
                        Go Back
                    </Button>
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="container-fluid">
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <h2>Review Details</h2>
                    <div className="d-flex gap-2">
                        <Button variant="secondary" onClick={() => navigate(-1)}>
                            <i className="fas fa-arrow-left me-2"></i>Back
                        </Button>
                        <Button variant="primary" onClick={handleEditClick}>
                            <i className="fas fa-edit me-2"></i>Edit Review
                        </Button>
                        <Button variant="danger" onClick={deleteReview}>
                            <i className="fas fa-trash me-2"></i>Delete Review
                        </Button>
                    </div>
                </div>

                <Row>
                    <Col md={8}>
                        <Card className="mb-4">
                            <Card.Header>
                                <h4>Review Information</h4>
                            </Card.Header>
                            <Card.Body>
                                <Row className="mb-3">
                                    <Col md={4}><strong>Review ID:</strong></Col>
                                    <Col md={8}>{reviewDetail._id}</Col>
                                </Row>
                                
                                <Row className="mb-3">
                                    <Col md={4}><strong>User Name:</strong></Col>
                                    <Col md={8}>{reviewDetail.reviewer_display_name || reviewDetail.source_reviewer_name || reviewDetail.user_id?.name || reviewDetail.title || "Shopper"}</Col>
                                </Row>

                                <Row className="mb-3">
                                    <Col md={4}><strong>User Email:</strong></Col>
                                    <Col md={8}>{reviewDetail.user_id?.email || "N/A"}</Col>
                                </Row>

                                <Row className="mb-3">
                                    <Col md={4}><strong>Heading:</strong></Col>
                                    <Col md={8}>{reviewDetail.heading || "N/A"}</Col>
                                </Row>

                                <Row className="mb-3">
                                    <Col md={4}><strong>Rating:</strong></Col>
                                    <Col md={8}>
                                        <Rating 
                                            name="read-only" 
                                            value={reviewDetail.rating} 
                                            readOnly 
                                            precision={0.5}
                                        />
                                        <span className="ms-2">({reviewDetail.rating}/5.0)</span>
                                    </Col>
                                </Row>

                                <Row className="mb-3">
                                    <Col md={4}><strong>Review Content:</strong></Col>
                                    <Col md={8}>
                                        <div className="border p-3 rounded bg-light">
                                            {reviewDetail.content || "No content provided"}
                                        </div>
                                    </Col>
                                </Row>

                                <Row className="mb-3">
                                    <Col md={4}><strong>Review Type:</strong></Col>
                                    <Col md={8}>
                                        {reviewDetail.review_type === 1 && <span className="badge bg-info">Image Review</span>}
                                        {reviewDetail.review_type === 2 && <span className="badge bg-warning">Video Review</span>}
                                        {reviewDetail.review_type === 3 && <span className="badge bg-success">Image & Video Review</span>}
                                        {reviewDetail.review_type === 0 && <span className="badge bg-secondary">Text Only</span>}
                                    </Col>
                                </Row>

                                <Row className="mb-3">
                                    <Col md={4}><strong>Created At:</strong></Col>
                                    <Col md={8}>
                                        {reviewDetail.created_at ? new Date(reviewDetail.created_at).toLocaleString() : "N/A"}
                                    </Col>
                                </Row>

                                <Row className="mb-3">
                                    <Col md={4}><strong>Last Updated:</strong></Col>
                                    <Col md={8}>
                                        {reviewDetail.updated_at ? new Date(reviewDetail.updated_at).toLocaleString() : "N/A"}
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>

                        {/* Review Media Files */}
                        {reviewDetail.review_files && reviewDetail.review_files.length > 0 && (
                            <Card className="mb-4">
                                <Card.Header>
                                    <h4>Review Media ({reviewDetail.review_files.length} files)</h4>
                                </Card.Header>
                                <Card.Body>
                                    <Row>
                                        {reviewDetail.review_files.map((file, idx) => (
                                            <Col md={4} key={idx} className="mb-3">
                                                <div className="position-relative">
                                                    {file.file_name && (
                                                        <>
                                                            {file.file_name.endsWith('.mp4') || 
                                                             file.file_name.includes('video') ||
                                                             file.media_type === 'video' ? (
                                                                <video 
                                                                    style={{ width: "100%", maxHeight: "250px" }} 
                                                                    controls
                                                                    className="border rounded"
                                                                >
                                                                    <source src={file.file_name} type="video/mp4" />
                                                                    Your browser does not support the video tag.
                                                                </video>
                                                            ) : (
                                                                <img 
                                                                    style={{ width: "100%", maxHeight: "250px", objectFit: "cover" }} 
                                                                    src={file.file_name} 
                                                                    alt={`Review media ${idx + 1}`}
                                                                    className="border rounded"
                                                                />
                                                            )}
                                                            {file.file_size && (
                                                                <small className="text-muted d-block mt-1">
                                                                    Size: {(parseInt(file.file_size) / 1024 / 1024).toFixed(2)} MB
                                                                </small>
                                                            )}
                                                        </>
                                                    )}
                                                </div>
                                            </Col>
                                        ))}
                                    </Row>
                                </Card.Body>
                            </Card>
                        )}
                    </Col>

                    <Col md={4}>
                        {/* Product Information */}
                        {reviewDetail.product_id && (
                            <Card className="mb-4">
                                <Card.Header>
                                    <h5>Product Information</h5>
                                </Card.Header>
                                <Card.Body>
                                    <Row className="mb-2">
                                        <Col><strong>Product Name:</strong></Col>
                                    </Row>
                                    <Row className="mb-3">
                                        <Col>{reviewDetail.product_id?.name || "N/A"}</Col>
                                    </Row>
                                    {reviewDetail.product_id?.product_images && reviewDetail.product_id.product_images.length > 0 && (
                                        <Row>
                                            <Col>
                                                <img 
                                                    src={reviewDetail.product_id.product_images[0]?.file_name || 
                                                         (reviewDetail.productImagePath && reviewDetail.product_id?._id 
                                                            ? `${reviewDetail.productImagePath}${reviewDetail.product_id._id}/${reviewDetail.product_id.product_images[0]?.file_name || reviewDetail.product_id.product_images[0]}` 
                                                            : reviewDetail.product_id.product_images[0])} 
                                                    alt="Product" 
                                                    style={{ width: "100%", maxHeight: "200px", objectFit: "cover" }}
                                                    className="border rounded"
                                                />
                                            </Col>
                                        </Row>
                                    )}
                                </Card.Body>
                            </Card>
                        )}

                        {/* Vendor Information */}
                        {reviewDetail.vendor_id && (
                            <Card className="mb-4">
                                <Card.Header>
                                    <h5>Vendor Information</h5>
                                </Card.Header>
                                <Card.Body>
                                    <Row className="mb-2">
                                        <Col><strong>Vendor Name:</strong></Col>
                                    </Row>
                                    <Row>
                                        <Col>{reviewDetail.vendor_id?.name || "N/A"}</Col>
                                    </Row>
                                </Card.Body>
                            </Card>
                        )}

                        {/* Status Information */}
                        <Card>
                            <Card.Header>
                                <h5>Status</h5>
                            </Card.Header>
                            <Card.Body>
                                <Row className="mb-2">
                                    <Col><strong>Active:</strong></Col>
                                    <Col>
                                        {reviewDetail.is_active === 1 ? (
                                            <span className="badge bg-success">Active</span>
                                        ) : (
                                            <span className="badge bg-danger">Inactive</span>
                                        )}
                                    </Col>
                                </Row>
                                <Row>
                                    <Col><strong>Deleted:</strong></Col>
                                    <Col>
                                        {reviewDetail.is_deleted === 1 ? (
                                            <span className="badge bg-danger">Deleted</span>
                                        ) : (
                                            <span className="badge bg-success">Not Deleted</span>
                                        )}
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            </div>

            {/* Edit Review Modal */}
            <Modal show={showEditModal} onHide={() => setShowEditModal(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Edit Review</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form onSubmit={submitEditedReview}>
                        <Row>
                            <Col lg={6} md={12}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Reviewer display name*</Form.Label>
                                    <Form.Control 
                                        type="text" 
                                        name="reviewer_display_name"
                                        value={editReview.reviewer_display_name}
                                        onChange={handleInputChange}
                                        placeholder="Enter reviewer display name"
                                    />
                                    {editReview.source_reviewer_name && (
                                        <div className="d-flex align-items-center justify-content-between gap-2 mt-2">
                                            <Form.Text>
                                                Source name: {editReview.source_reviewer_name}
                                            </Form.Text>
                                            <Button
                                                type="button"
                                                size="sm"
                                                variant="outline-secondary"
                                                onClick={() => setEditReview(prev => ({
                                                    ...prev,
                                                    reviewer_display_name: prev.source_reviewer_name
                                                }))}
                                            >
                                                Restore source name
                                            </Button>
                                        </div>
                                    )}
                                </Form.Group>
                            </Col>
                            <Col lg={6} md={12}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Title*</Form.Label>
                                    <Form.Control 
                                        type="text" 
                                        name="heading"
                                        value={editReview.heading} 
                                        onChange={handleInputChange}
                                        placeholder="Enter review title"
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                        
                        <Form.Group className="mb-3">
                            <Form.Label>Rating*</Form.Label>
                            <div>
                                <Rating 
                                    name="rating"
                                    value={editReview.rating} 
                                    onChange={handleRatingChange}
                                    precision={0.5}
                                    className="rating"
                                />
                            </div>
                        </Form.Group>
                        
                        <Form.Group className="mb-3">
                            <Form.Label>Comments*</Form.Label>
                            <Form.Control 
                                as="textarea" 
                                rows={3} 
                                name="content"
                                value={editReview.content} 
                                onChange={handleInputChange}
                                placeholder="Enter your review comments"
                            />
                        </Form.Group>
                        
                        <Form.Group className="mb-3">
                            <Form.Label>Current Review Media</Form.Label>
                            <div className="d-flex flex-wrap gap-2">
                                {editReview.review_files && editReview.review_files.length > 0 ? (
                                    editReview.review_files.map((file, idx) => {
                                        const isMarkedForRemoval = filesToRemove.includes(file._id);
                                        return (
                                            <div key={idx} className="position-relative" style={{ opacity: isMarkedForRemoval ? 0.5 : 1 }}>
                                                {file.file_name && (
                                                    <>
                                                        {file.file_name.endsWith('.mp4') || file.file_name.includes('video') ? (
                                                            <video style={{ width: "100px", height: "100px", objectFit: "cover" }} controls>
                                                                <source src={file.file_name} type="video/mp4" />
                                                                Your browser does not support the video.
                                                            </video>
                                                        ) : (
                                                            <img style={{ width: "100px", height: "100px", objectFit: "cover" }} src={file.file_name} alt='' />
                                                        )}
                                                        <Button 
                                                            variant={isMarkedForRemoval ? "success" : "danger"}
                                                            size="sm"
                                                            style={{ position: "absolute", top: "5px", right: "5px", padding: "2px 5px" }}
                                                            onClick={() => isMarkedForRemoval ? handleRestoreFile(file._id) : handleRemoveExistingFile(file._id)}
                                                        >
                                                            {isMarkedForRemoval ? "Restore" : "X"}
                                                        </Button>
                                                    </>
                                                )}
                                            </div>
                                        );
                                    })
                                ) : (
                                    <span>No media files available</span>
                                )}
                            </div>
                        </Form.Group>
                        
                        <Form.Group className="mb-3">
                            <Form.Label>Upload New Files (Max 5)</Form.Label>
                            <Form.Control 
                                type="file" 
                                name="review_files"
                                accept="image/*,video/*"
                                onChange={handlePhoto}
                                multiple
                            />
                            <small className="text-muted">
                                Supported formats: Images and Videos
                            </small>
                        </Form.Group>
                        
                        {editReview.fileNames && editReview.fileNames.length > 0 && (
                            <div className="mb-3">
                                <strong>Selected Files:</strong>
                                <ul>
                                    {editReview.fileNames.map((fileName, index) => (
                                        <li key={index}>{fileName}</li>
                                    ))}
                                </ul>
                            </div>
                        )}
                        
                        <div className="d-flex justify-content-end gap-2">
                            <Button variant="secondary" onClick={() => setShowEditModal(false)}>
                                Cancel
                            </Button>
                            <Button variant="primary" type="submit">
                                Save Changes
                            </Button>
                        </div>
                    </Form>
                </Modal.Body>
            </Modal>
            
            {/* Snackbar for success/error messages */}
            <Snackbar 
                open={successSnackbarOpen} 
                autoHideDuration={3000} 
                onClose={handleSnackbarClose} 
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <MuiAlert onClose={handleSnackbarClose} severity="success" sx={{ width: '100%' }}>
                    {snackbarMessage}
                </MuiAlert>
            </Snackbar>
            <Snackbar 
                open={errorSnackbarOpen} 
                autoHideDuration={3000} 
                onClose={handleSnackbarClose} 
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <MuiAlert onClose={handleSnackbarClose} severity="error" sx={{ width: '100%' }}>
                    {snackbarMessage}
                </MuiAlert>
            </Snackbar>
        </Layout>
    );
};

export default ViewReviewDetail;

